// "Talk to log": turn a sentence like "wee and poo outside 10 minutes ago,
// then he ate all his lunch" into proposed log entries.
//
// The server can do this with Claude (better at messy sentences); this rule
// parser is the offline fallback and handles the everyday phrases.

import type { Appetite, GroomTask, LogEvent, Millis, PooQuality, Symptom } from './types.ts';
import { MINUTE } from './time.ts';

export type ProposalType = 'wee' | 'poo' | 'meal' | 'water' | 'treat' | 'nap' | 'woke' | 'walk' | 'play' | 'training' | 'weight' | 'groom' | 'health' | 'meds' | 'note';

export interface LogProposal {
  type: ProposalType;
  /** When it happened (for things with a duration: when it ended). */
  minutesAgo: number;
  durationMin?: number;
  where?: 'outside' | 'inside';
  quality?: PooQuality;
  appetite?: Appetite;
  grams?: number;
  kg?: number;
  /** Treat, medicine name or note text. */
  what?: string;
  dose?: string;
  symptom?: Symptom;
  severity?: 1 | 2 | 3;
  tasks?: GroomTask[];
  skills?: string[];
}

export const PROPOSAL_TYPES: ProposalType[] = ['wee', 'poo', 'meal', 'water', 'treat', 'nap', 'woke', 'walk', 'play', 'training', 'weight', 'groom', 'health', 'meds', 'note'];

const NUM: Record<string, number> = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, ten: 10, fifteen: 15, twenty: 20, thirty: 30, forty: 40, 'forty-five': 45, half: 0.5, couple: 2 };

function num(s: string): number {
  return NUM[s.toLowerCase()] ?? Number(s);
}

/** Minutes before `now` for phrases like "10 mins ago", "half an hour ago", "at 3pm", "at 14:30". */
export function parseWhen(text: string, now: Millis): number | undefined {
  const t = text.toLowerCase();
  if (/\bhalf an hour ago\b/.test(t)) return 30;
  let m = /\b(\d+|a|an|one|two|three|four|five|ten|fifteen|twenty|thirty|forty|forty-five|couple of)\s*(?:min(?:ute)?s?|m)\b\s*ago/.exec(t);
  if (m) return num(m[1].replace(' of', ''));
  m = /\b(\d+(?:\.\d+)?|a|an|one|two|three|couple of)\s*(?:hours?|hrs?|h)\b\s*ago/.exec(t);
  if (m) return Math.round(num(m[1].replace(' of', '')) * 60);
  m = /\b(?:at|around|about)\s+(\d{1,2})(?::|\.)?(\d{2})?\s*(am|pm)?\b/.exec(t);
  if (m && (m[2] || m[3])) {
    let h = Number(m[1]);
    const min = Number(m[2] ?? 0);
    if (m[3] === 'pm' && h < 12) h += 12;
    if (m[3] === 'am' && h === 12) h = 0;
    const d = new Date(now);
    let at = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, min).getTime();
    // "at 11" said at 1am means last night; "at 2pm" said at 1pm without am/pm can't be the future.
    if (at > now + 5 * MINUTE) at -= 24 * 60 * MINUTE;
    return Math.max(0, Math.round((now - at) / MINUTE));
  }
  if (/\bjust (now|then)\b|\bjust\b/.test(t)) return 0;
  return undefined;
}

/** Today's (or last night's) clock time at or before `limit`; with no am/pm, the most recent of the morning or afternoon reading. */
function clockAtOrBefore(h: number, min: number, ampm: string | undefined, limit: Millis): Millis {
  const d = new Date(limit);
  const hours = ampm === 'pm' ? [h < 12 ? h + 12 : h] : ampm === 'am' ? [h === 12 ? 0 : h] : h < 12 ? [h, h + 12] : [h];
  const times = hours.flatMap((hh) => [0, 1].map((back) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - back, hh, min).getTime()));
  return Math.max(...times.filter((t) => t <= limit + 5 * MINUTE));
}

const CLOCK = String.raw`(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?|half\s+(\d{1,2})`;
const RANGE = new RegExp(String.raw`\b(?:from|between)\s+(?:${CLOCK})\s*(?:till|until|til|to|and|-|–)\s*(?:${CLOCK})\b`);

/** "from 1 till 2:30", "between 13:00 and 14:30", "from half 12 to 2pm": when it ended (minutes ago) and how long it lasted. */
export function parseRange(text: string, now: Millis): { minutesAgo: number; durationMin: number } | undefined {
  const m = RANGE.exec(text.toLowerCase());
  if (!m) return undefined;
  const read = (h?: string, mm?: string, ap?: string, half?: string) => (half ? { h: Number(half), min: 30, ap } : { h: Number(h), min: Number(mm ?? 0), ap });
  const a = read(m[1], m[2], m[3], m[4]);
  const b = read(m[5], m[6], m[7], m[8]);
  if (a.h > 23 || b.h > 23 || a.min > 59 || b.min > 59) return undefined;
  // "from 11 till 1pm": the start takes the end's am/pm only if that keeps it before the end.
  const end = clockAtOrBefore(b.h, b.min, b.ap, now);
  const start = clockAtOrBefore(a.h, a.min, a.ap, end - MINUTE);
  const durationMin = Math.round((end - start) / MINUTE);
  if (durationMin <= 0 || durationMin > 14 * 60) return undefined;
  return { minutesAgo: Math.max(0, Math.round((now - end) / MINUTE)), durationMin };
}

function duration(text: string): number | undefined {
  const t = text.toLowerCase();
  if (/\bhalf an hour\b/.test(t) && !/half an hour ago/.test(t)) return 30;
  const m = /\b(?:for|a|an)?\s*(\d+|ten|fifteen|twenty|thirty|forty|forty-five)[\s-]*(?:min(?:ute)?s?|m)\b(?!\s*ago)/.exec(t);
  if (m) return num(m[1]);
  const h = /\b(\d+(?:\.\d+)?|an|one|two)[\s-]*(?:hours?|hrs?|h)\b(?!\s*ago)/.exec(t);
  if (h) return Math.round(num(h[1]) * 60);
  return undefined;
}

const INSIDE = /\b(inside|indoors|accident|on the (?:floor|rug|carpet|mat|sofa|bed)|in the (?:flat|house|hall|kitchen|lounge|crate))\b/;

/** Rule-based parse. Returns proposals in the order mentioned. */
export function parseLogText(text: string, now: Millis = Date.now()): LogProposal[] {
  const out: LogProposal[] = [];
  const whole = parseWhen(text, now);
  // Split into clauses on sentence breaks, "then", "and then", commas.
  const clauses = text
    .split(/(?:(?<!\d)\.|\.(?!\d)|[;!?]|,|\band then\b|\bthen\b|\balso\b)+/i)
    .map((c) => c.trim())
    .filter(Boolean);
  // A clause with no event ("Accident on the rug, a wee") lends its place and time to the next.
  let carry: { inside?: boolean; when?: number } = {};
  for (const clause of clauses) {
    const c = clause.toLowerCase();
    const range = parseRange(c, now);
    const when = range?.minutesAgo ?? parseWhen(c, now) ?? carry.when ?? whole ?? 0;
    const where = INSIDE.test(c) || carry.inside ? 'inside' : 'outside';
    const before = out.length;
    carry = {};
    const push = (p: Omit<LogProposal, 'minutesAgo'> & { minutesAgo?: number }) => {
      // "Napped from 1 till 2:30", "slept between 1 and 3": a nap (or outing, play) with both ends known.
      if (range && (p.type === 'nap' || p.type === 'woke' || p.type === 'walk' || p.type === 'play')) p = { ...p, type: p.type === 'woke' ? 'nap' : p.type, durationMin: range.durationMin };
      out.push({ minutesAgo: when, ...p });
    };
    parseClause(c, clause, where, push);
    if (out.length === before) carry = { inside: INSIDE.test(c) || undefined, when: parseWhen(c, now) };
  }
  return out;
}

function parseClause(c: string, clause: string, where: 'inside' | 'outside', push: (p: Omit<LogProposal, 'minutesAgo'>) => void): void {
  {

    const wee = /\b(wee+d?|wees|weeing|pee+d?|pees|peeing|piddle[ds]?|widdle[ds]?|toilet|pish)\b/.test(c);
    const poo = /\b(poo+[ps]?|poo+ed|pooing|number two|dump|jobbie)\b/.test(c);
    if (wee || poo) {
      const quality: PooQuality | undefined = /\brunny|diarrh/.test(c) ? 'runny' : /\bsoft|loose\b/.test(c) ? 'soft' : /\bhard\b/.test(c) ? 'hard' : undefined;
      // "toilet" alone counts as a wee.
      if (wee || (!poo && /toilet/.test(c))) push({ type: 'wee', where });
      if (poo) push({ type: 'poo', where, ...(quality ? { quality } : {}) });
      return;
    }
    // Health before meals: "was sick after breakfast" is a health note, not a meal.
    if (/\b(sick|vomit(?:ed)?|threw up|diarrh(?:o)?ea|runny poo|limp(?:ing)?|cough(?:ing)?|scratch(?:ing)?|itch(?:y|ing)?|shaking (?:his )?head|lethargic|off his food|ate (?:a|some) (?:sock|stone|leaf|stick|plastic|something))\b/.test(c)) {
      const symptom: Symptom = /sick|vomit|threw up/.test(c)
        ? 'vomit'
        : /diarrh|runny/.test(c)
          ? 'diarrhoea'
          : /limp/.test(c)
            ? 'limping'
            : /cough/.test(c)
              ? 'coughing'
              : /scratch|itch/.test(c)
                ? 'itching'
                : /head/.test(c)
                  ? 'ear-shaking'
                  : /lethargic/.test(c)
                    ? 'lethargic'
                    : /off his food/.test(c)
                      ? 'not-eating'
                      : 'ate-something';
      push({ type: 'health', symptom, what: clause });
      return;
    }
    if (/\b(ate|eaten|eat|fed|feed|breakfast|lunch|dinner|tea|supper|meal|kibble|food)\b/.test(c) && !/\bate (?:a|some) (?:sock|stone|leaf|stick|plastic)/.test(c)) {
      const appetite: Appetite | undefined = /\b(didn'?t eat|wouldn'?t eat|off (?:his )?food|none|refused|nothing)\b/.test(c)
        ? 'none'
        : /\b(half|some|a bit|picked|left (?:some|a bit|half))\b/.test(c)
          ? 'some'
          : /\bmost\b/.test(c)
            ? 'most'
            : /\b(all|everything|wolfed|inhaled|scoffed|cleared|licked the bowl)\b/.test(c)
              ? 'all'
              : undefined;
      const g = /(\d+)\s*g(?:rams?)?\b/.exec(c);
      push({ type: 'meal', ...(appetite ? { appetite } : {}), ...(g ? { grams: Number(g[1]) } : {}) });
      return;
    }
    if (/\b(woke|wakes|waking|awake|up from (?:his|a) nap|got up)\b/.test(c)) {
      const d = duration(c);
      if (d) push({ type: 'nap', durationMin: d });
      else push({ type: 'woke' });
      return;
    }
    if (/\b(nap|naps|napped|napping|asleep|sleeping|slept|gone down|went down|bed ?time|in (?:his|the) crate for a sleep|snoozing)\b/.test(c)) {
      const d = duration(c);
      push({ type: 'nap', ...(d ? { durationMin: d } : {}) });
      return;
    }
    if (/\b(walk|walked|outing|out and about|carried (?:him )?(?:out|round)|stroll|park|green)\b/.test(c)) {
      const d = duration(c);
      push({ type: 'walk', ...(d ? { durationMin: d } : {}) });
      return;
    }
    if (/\bweigh|\d\s*(?:kg|kilos?)\b/.test(c)) {
      const k = /(\d+(?:\.\d+)?)\s*(?:kg|kilos?)/.exec(c) ?? /weigh(?:s|ed)?\s*(\d+(?:\.\d+)?)/.exec(c);
      if (k) {
        push({ type: 'weight', kg: Number(k[1]) });
        return;
      }
    }
    if (/\b(brush(?:ed)?|groom(?:ed)?|nails?|bath(?:ed)?|ears? clean(?:ed)?|cleaned (?:his )?ears|teeth)\b/.test(c)) {
      const tasks: GroomTask[] = [];
      if (/brush/.test(c) && !/teeth/.test(c)) tasks.push('brush');
      if (/ear/.test(c)) tasks.push('ears');
      if (/nail/.test(c)) tasks.push('nails');
      if (/teeth/.test(c)) tasks.push('teeth');
      if (/bath/.test(c)) tasks.push('bath');
      if (!tasks.length) tasks.push('brush');
      push({ type: 'groom', tasks });
      return;
    }
    if (/\b(train(?:ing|ed)?|practi[sc]ed?|session|sit|recall|hand touch|leave it|name game)\b/.test(c)) {
      const skills: string[] = [];
      for (const [re, id] of [
        [/\bsit\b/, 'sit'],
        [/\bdown\b/, 'down'],
        [/recall|come/, 'recall'],
        [/hand ?touch|touch/, 'hand-touch'],
        [/leave it/, 'leave-it'],
        [/name game|his name/, 'name-game'],
        [/settle/, 'settle'],
        [/crate/, 'crate-love'],
        [/wait/, 'wait'],
        [/whistle/, 'whistle-recall'],
      ] as [RegExp, string][])
        if (re.test(c)) skills.push(id);
      push({ type: 'training', skills });
      return;
    }
    if (/\b(play(?:ed|ing)?|zoomies|tug|fetch)\b/.test(c)) {
      const d = duration(c);
      push({ type: 'play', ...(d ? { durationMin: d } : {}) });
      return;
    }
    if (/\b(drank|drink|water)\b/.test(c)) {
      push({ type: 'water' });
      return;
    }
    if (/\b(chew|treat|kong|lickimat|lick mat|bully stick|biscuit)\b/.test(c)) {
      const m = /\b(kong|lickimat|lick mat|bully stick|biscuit|chew)\b/.exec(c);
      push({ type: 'treat', what: m ? m[1][0].toUpperCase() + m[1].slice(1) : undefined });
      return;
    }
    if (/\b(wormer|worming|flea|tablet|pill|medicine|drops|milbemax|nexgard|bravecto|drontal|advocate|simparica)\b/.test(c)) {
      const m = /\b(milbemax|nexgard|bravecto|drontal|advocate|simparica|wormer|flea treatment|ear drops|eye drops|tablet|pill|medicine)\b/.exec(c);
      push({ type: 'meds', what: m ? m[1][0].toUpperCase() + m[1].slice(1) : 'Medication' });
      return;
    }
  }
}

/** Short label for a proposal chip, e.g. "Wee outside · 10 min ago". */
export function describeProposal(p: LogProposal, skillName?: (id: string) => string | undefined): string {
  const when = p.minutesAgo <= 1 ? 'just now' : p.minutesAgo < 90 ? `${p.minutesAgo} min ago` : `${Math.round(p.minutesAgo / 60)} h ago`;
  const dur = p.durationMin ? ` (${p.durationMin} min)` : '';
  let what: string;
  switch (p.type) {
    case 'wee':
      what = p.where === 'inside' ? 'Wee indoors (accident)' : 'Wee outside';
      break;
    case 'poo':
      what = `${p.where === 'inside' ? 'Poo indoors (accident)' : 'Poo outside'}${p.quality && p.quality !== 'firm' ? ` · ${p.quality}` : ''}`;
      break;
    case 'meal':
      what = `Meal${p.grams ? ` ${p.grams} g` : ''}${p.appetite ? ` · ate ${p.appetite === 'all' ? 'it all' : p.appetite}` : ''}`;
      break;
    case 'nap':
      what = p.durationMin ? `Nap${dur}` : 'Gone down for a nap';
      break;
    case 'woke':
      what = 'Woke up';
      break;
    case 'walk':
      what = `Outing${dur}`;
      break;
    case 'play':
      what = `Play${dur}`;
      break;
    case 'training':
      what = `Training${p.skills?.length ? ` · ${p.skills.map((s) => skillName?.(s) ?? s).join(', ')}` : ''}`;
      break;
    case 'weight':
      what = `Weighed ${p.kg} kg`;
      break;
    case 'groom':
      what = `Grooming · ${(p.tasks ?? []).join(', ')}`;
      break;
    case 'health':
      what = `Health note · ${p.symptom ?? 'other'}`;
      break;
    case 'meds':
      what = `${p.what ?? 'Medication'}${p.dose ? ` · ${p.dose}` : ''}`;
      break;
    case 'treat':
      what = p.what ?? 'Chew / treat';
      break;
    case 'water':
      what = 'Drank water';
      break;
    default:
      what = p.what ?? 'Note';
  }
  return `${what} · ${when}`;
}

/**
 * Build the log event for a proposal. `nap` without a duration and `woke`
 * are handled by the caller (they open/close the current sleep).
 */
export function proposalToEvent(p: LogProposal, now: Millis, id: string, by?: string): LogEvent | undefined {
  const at = now - p.minutesAgo * MINUTE;
  const base = { id, by, source: 'app' as const, note: p.type === 'health' || p.type === 'note' ? p.what : undefined };
  const span = () => (p.durationMin ? { at: at - p.durationMin * MINUTE, end: at } : { at });
  switch (p.type) {
    case 'wee':
      return { ...base, at, type: 'wee', data: { where: p.where ?? 'outside' } };
    case 'poo':
      return { ...base, at, type: 'poo', data: { where: p.where ?? 'outside', ...(p.quality ? { quality: p.quality } : {}) } };
    case 'meal':
      return { ...base, at, type: 'meal', data: { ...(p.grams ? { grams: p.grams } : {}), ...(p.appetite ? { appetite: p.appetite } : {}) } };
    case 'water':
      return { ...base, at, type: 'water', data: {} };
    case 'treat':
      return { ...base, at, type: 'treat', data: { what: p.what } };
    case 'nap':
      return p.durationMin ? { ...base, ...span(), type: 'sleep', data: {} } : undefined;
    case 'walk':
      return { ...base, ...span(), type: 'walk', data: {} };
    case 'play':
      return { ...base, ...span(), type: 'play', data: {} };
    case 'training':
      return { ...base, at, type: 'training', data: { skills: p.skills ?? [] } };
    case 'weight':
      return p.kg && p.kg > 0 && p.kg < 60 ? { ...base, at, type: 'weight', data: { kg: p.kg } } : undefined;
    case 'groom':
      return { ...base, at, type: 'groom', data: { tasks: p.tasks?.length ? p.tasks : ['brush'] } };
    case 'health':
      return { ...base, at, type: 'health', data: { symptom: p.symptom ?? 'other', ...(p.severity ? { severity: p.severity } : {}) } };
    case 'meds':
      return { ...base, at, type: 'meds', data: { name: p.what ?? 'Medication', ...(p.dose ? { dose: p.dose } : {}) } };
    case 'note':
      return { ...base, at, type: 'note', data: {} };
    default:
      return undefined;
  }
}

/** Keep only well-formed proposals (used on AI output). */
export function sanitiseProposals(list: unknown): LogProposal[] {
  if (!Array.isArray(list)) return [];
  return list
    .filter((p): p is LogProposal => !!p && typeof p === 'object' && PROPOSAL_TYPES.includes((p as LogProposal).type))
    .map((p) => ({ ...p, minutesAgo: Math.max(0, Math.min(24 * 60, Math.round(Number(p.minutesAgo) || 0))), ...(p.durationMin ? { durationMin: Math.max(1, Math.min(600, Math.round(p.durationMin))) } : {}) }))
    .slice(0, 12);
}
